import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../states/user/user-selector';
import { ContactUsService } from '../../../services/website/contact-us.service';
import { ContactPurpose } from '../../../models/enums/contactUs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-clarity-call-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, ButtonModule, InputTextareaModule, ToastModule],
  providers: [MessageService],
  templateUrl: './clarity-call-popup.component.html',
  styleUrl: './clarity-call-popup.component.scss'
})
export class ClarityCallPopupComponent implements OnInit {
  /** Controls dialog visibility — supports two-way binding [(visible)] */
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Which ContactPurpose enum value to send with the API — defaults to MentorClarityCall */
  @Input() contactPurpose: ContactPurpose = ContactPurpose.MentorClarityCall;

  /** Optional overrides — if not set, defaults are derived from contactPurpose */
  @Input() headline?: string;
  @Input() bodyText?: string;
  @Input() inputPlaceholder?: string;
  @Input() ctaLabel?: string;

  query: string = '';
  isSending: boolean = false;
  currentUser: any;

  get displayHeadline(): string {
    if (this.headline) return this.headline;
    if (this.isDirectApplyContext) return `Confused about your application process?<br>Let's sort it.`;
    return `Been overthinking?<br>Drop it here.`;
  }

  get displayBody(): string {
    if (this.bodyText) return this.bodyText;
    if (this.isDirectApplyContext) return `You don't have to figure out your application alone. Whether it's choosing universities, picking an apply option, or getting through the form — drop it here and we'll help you move forward.`;
    return `You don't have to sort everything alone, neither do you have to have everything figured out. Just share where you're at, and we'll help you take the right next step.`;
  }

  get displayPlaceholder(): string {
    if (this.inputPlaceholder) return this.inputPlaceholder;
    if (this.isDirectApplyContext) return 'Stuck on a step? Tell us here';
    return 'Type your query here';
  }

  get displayCta(): string {
    if (this.ctaLabel) return this.ctaLabel;
    if (this.isDirectApplyContext) return 'Get help';
    return 'Send';
  }

  get isScheduleACallContext(): boolean {
    return Number(this.contactPurpose) === 10;
  }

  get isDirectApplyContext(): boolean {
    const purpose = Number(this.contactPurpose);
    return (
      purpose === ContactPurpose.DirectApplyContactUs ||
      purpose === ContactPurpose.ExpertCheckoutContactUs
    );
  }

  constructor(
    private store: Store,
    private contactUsService: ContactUsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.store.select(UserSelectors.getUser).subscribe((user: any) => {
      this.currentUser = user;
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  send(): void {
    if (!this.query.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Please type your query before sending.'
      });
      return;
    }

    this.isSending = true;
    this.contactUsService.submitContactUsForm({
      contactPurpose: this.contactPurpose,
      firstName: this.currentUser?.firstName,
      lastName: this.currentUser?.lastName,
      email: this.currentUser?.email,
      phoneNumber: this.currentUser?.phoneNumber,
      message: this.query
    }).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Request Sent!',
          detail: 'Your request has been submitted. Our team will reach out to you shortly.'
        });
        this.isSending = false;
        this.query = '';
        this.close();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong. Please try again.'
        });
        this.isSending = false;
      }
    });
  }
}
